import { Fragment, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { ChevronDown } from 'lucide-react'

import styles from './AnnotationLeaderboardPage.module.scss'

import {
  getMeetingAttributionGtLeaderboard,
  type GtLeaderboardOs,
  type GtLeaderboardPlatform,
  type MeetingAttributionGtLeaderboardPlatform,
  type MeetingAttributionGtLeaderboardResponse,
} from '@/api/internal'
import { annotateHref } from '@/pages/app/AttributionViewer/mode'

const PLATFORM_LABELS: Record<GtLeaderboardPlatform, string> = {
  google_meet: 'Google Meet',
  zoom: 'Zoom',
  teams: 'Teams',
  slack: 'Slack',
  in_person: 'In person',
  other: 'Other',
  unknown: 'Unknown',
}
const OS_LABELS: Record<GtLeaderboardOs, string> = {
  macos: 'macOS',
  windows: 'Windows',
  unknown: '—',
}

const share = (part: number, whole: number) => (whole > 0 ? part / whole : 0)
const percent = (value: number) => `${Math.round(value * 100)}%`
const shortDate = (value: string) =>
  new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
const duration = (durationMs: number | null) => {
  if (durationMs === null) return '—'
  const seconds = Math.floor(durationMs / 1000)
  const paddedSeconds = String(seconds % 60).padStart(2, '0')
  if (seconds >= 3600) {
    return `${Math.floor(seconds / 3600)}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}:${paddedSeconds}`
  }
  return `${Math.floor(seconds / 60)}:${paddedSeconds}`
}
const progress = (reviewed: number | null, total: number | null) =>
  reviewed === null || total === null || total === 0
    ? '—'
    : `${Math.round((reviewed / total) * 100)}% · ${reviewed}/${total} turns`

const Stat = ({ value, label, tone = 'good' }: { value: number; label: string; tone?: 'good' | 'warn' }) => (
  <div className={styles.stat}>
    <strong className={clsx(styles.statValue, tone === 'warn' && styles.warn)}>{value}</strong>
    <span>{label}</span>
  </div>
)

const PlatformCoverage = ({ platforms }: { platforms: MeetingAttributionGtLeaderboardPlatform[] }) => {
  const annotatedTotal = platforms.reduce((sum, p) => sum + p.annotated, 0)
  const baselineTotal = platforms.reduce((sum, p) => sum + p.baseline, 0)
  const rows = platforms.map((p) => ({
    ...p,
    annotatedShare: share(p.annotated, annotatedTotal),
    baselineShare: p.platform === 'unknown' ? null : share(p.baseline, baselineTotal),
  }))
  const scale = Math.max(...rows.flatMap((r) => [r.annotatedShare, r.baselineShare ?? 0]), 0) || 1

  return (
    <section className={styles.card} aria-label="Coverage across meeting platforms">
      <p className={styles.cardTitle} aria-hidden>
        Coverage across meeting platforms
      </p>
      <ul className={styles.platforms}>
        {rows.map((row) => (
          <li key={row.platform} className={styles.platform}>
            <span className={styles.platformName}>{PLATFORM_LABELS[row.platform]}</span>
            <span className={styles.track} aria-hidden>
              {row.baselineShare !== null && (
                <span className={styles.baselineBar} style={{ width: `${(row.baselineShare / scale) * 100}%` }} />
              )}
              <span
                className={clsx(styles.annotatedBar, row.platform === 'unknown' && styles.unknownBar)}
                style={{ width: `${(row.annotatedShare / scale) * 100}%` }}
              />
            </span>
            <span className={styles.platformValue}>
              <strong>{row.annotated}</strong> · {percent(row.annotatedShare)}
              {row.baselineShare !== null && <span className={styles.vs}> vs {percent(row.baselineShare)}</span>}
            </span>
          </li>
        ))}
      </ul>
      <div className={styles.legend}>
        <span>
          <i className={styles.legendAnnotated} aria-hidden />
          Annotated meetings (count · share of annotated)
        </span>
        <span>
          <i className={styles.legendBaseline} aria-hidden />
          Share of all real multi-speaker meetings, last 30 days, where the platform is known
        </span>
      </div>
    </section>
  )
}

const Leaderboard = ({ people }: { people: MeetingAttributionGtLeaderboardResponse['people'] }) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, 'completed' | 'incomplete' | null>>({})

  const toggleList = (rowKey: string, list: 'completed' | 'incomplete') => {
    setExpandedRows((current) => ({ ...current, [rowKey]: current[rowKey] === list ? null : list }))
  }

  return (
    <section className={styles.tableCard}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Completed</th>
            <th scope="col">Incomplete</th>
          </tr>
        </thead>
        <tbody>
          {people.map((person) => {
            const rowKey = person.name
            const hasIncomplete = person.incomplete.length > 0
            const expandedList = expandedRows[rowKey]
            const renderCount = (list: 'completed' | 'incomplete') => {
              const count = person[list].length
              if (count === 0) return count
              return (
                <button
                  type="button"
                  className={clsx(styles.countButton, list === 'incomplete' && hasIncomplete && styles.warn)}
                  aria-label={`${list === 'completed' ? 'Completed' : 'Incomplete'} meetings for ${person.name} (${count})`}
                  aria-expanded={expandedList === list}
                  onClick={() => toggleList(rowKey, list)}
                >
                  {count}
                  <ChevronDown
                    className={clsx(styles.chevron, expandedList === list && styles.chevronOpen)}
                    size={12}
                    aria-hidden
                  />
                </button>
              )
            }

            return (
              <Fragment key={rowKey}>
                <tr className={clsx(hasIncomplete && styles.incomplete)} data-incomplete={hasIncomplete || undefined}>
                  <th scope="row">{person.name}</th>
                  <td>{renderCount('completed')}</td>
                  <td>{renderCount('incomplete')}</td>
                </tr>
                {expandedList && (
                  <tr className={styles.detailRow}>
                    <td colSpan={3}>
                      <div className={styles.meetingTableScroll}>
                        <table
                          className={clsx(
                            styles.meetingTable,
                            expandedList === 'incomplete' && styles.meetingTableWithProgress
                          )}
                        >
                          <thead>
                            <tr>
                              <th scope="col">Meeting</th>
                              <th scope="col">Date</th>
                              <th scope="col" className={styles.numeric}>
                                Speakers
                              </th>
                              <th scope="col" className={styles.numeric}>
                                Length
                              </th>
                              <th scope="col">Platform</th>
                              <th scope="col">OS</th>
                              {expandedList === 'incomplete' && (
                                <th scope="col" className={styles.numeric}>
                                  Progress
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {person[expandedList].map((meeting) => (
                              <tr key={meeting.meeting_id}>
                                <td>
                                  <Link className={styles.meetingLink} to={annotateHref(meeting.meeting_id)}>
                                    {meeting.title || 'Untitled meeting'}
                                  </Link>
                                </td>
                                <td className={styles.meetingDate}>
                                  {meeting.created_at ? (
                                    <time dateTime={meeting.created_at}>{shortDate(meeting.created_at)}</time>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className={styles.numeric}>{meeting.speaker_count ?? '—'}</td>
                                <td className={styles.numeric}>{duration(meeting.duration_ms)}</td>
                                <td>{PLATFORM_LABELS[meeting.platform]}</td>
                                <td>{OS_LABELS[meeting.os]}</td>
                                {expandedList === 'incomplete' && (
                                  <td className={styles.numeric}>
                                    {progress(meeting.reviewed_turns, meeting.total_turns)}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
      {people.length === 0 && <p className={styles.empty}>No one has annotated a meeting yet.</p>}
    </section>
  )
}

const AnnotationLeaderboardPage = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attribution-gt-leaderboard'],
    queryFn: async ({ signal }) => (await getMeetingAttributionGtLeaderboard(signal)).data,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })

  const asOf = data
    ? new Date(data.generated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null

  return (
    <main className={styles.page}>
      <Helmet>
        <title>Annotation progress</title>
      </Helmet>
      <div className={styles.content}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>Speaker-attribution ground truth</p>
          <h1>Annotation Progress</h1>
          <p className={styles.subtitle}>
            Meetings started versus completed in the Attribution Viewer annotation tool
            {asOf ? `, as of ${asOf}` : ''}. Sorted by meetings completed.
          </p>
        </header>

        {isLoading && <p className={styles.status}>Loading annotation progress…</p>}
        {isError && (
          <p className={styles.status} role="alert">
            Couldn&apos;t load annotation progress.{' '}
            <button type="button" className={styles.retry} onClick={() => void refetch()}>
              Try again
            </button>
          </p>
        )}

        {data && (
          <>
            <div className={styles.stats}>
              <Stat value={data.completed_meetings} label="meetings completed" />
              <Stat value={data.started_meetings} label="meetings started" />
              <Stat
                value={Math.max(data.started_meetings - data.completed_meetings, 0)}
                label="started, not completed"
                tone="warn"
              />
              <Stat value={data.people.length} label="people involved" />
            </div>
            <PlatformCoverage platforms={data.platforms} />
            <Leaderboard people={data.people} />
          </>
        )}
      </div>
    </main>
  )
}

export default AnnotationLeaderboardPage
